/**
 * Side Cart Component
 * 
 * Main side cart drawer component that displays cart items and totals.
 * Replicates TheSpadr sidecart behavior.
 */

'use client';

import styled from '@emotion/styled';
import {
  Drawer,
} from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n';
import { useIntl } from '@/lib/intl';
import { SIDE_CART_ACCESSIBILITY_LABEL, SIDE_CART_LOADING } from './i18.keys';
import {
  useCart,
  useUpdateCartItem,
  useRemoveCartItem,
  useUpdatePackQuantity,
  useRemovePackFromCart,
} from '@/lib/hooks/use-cart';
import {
  flattenCartDiscountRows,
  pricingSavingsCents,
} from '@template-project/storefront-bff-client';
import { CartHeader } from './CartHeader/CartHeader';
import { CartItems } from './CartItems/CartItems';
import { EmptyCart } from './EmptyCart/EmptyCart';
import { CartSummaryV2 } from './CartSummaryV2';
import type { CartSummaryVariant } from './CartSummaryV2';
import { storefrontLayoutLogger } from '@/lib/logger';
import { useFunnelStepEmitters } from '@template-project/storefront-funnel';
import {
  buildBrowserBusinessEventId,
  EVENT_TYPES,
  trackAddToCartFromLines,
} from '@/lib/analytics';
import { toFunnelCartPageContext } from '@/lib/funnel-cart-context';

const DrawerWrapper = styled.div`
  > div > div {
    z-index: 1000;
  }
`;

const Main = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Lines = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  padding: 0 ${({ theme }) => theme.space.md};
`;

const Footer = styled.div`
  padding: ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
  box-shadow: 0px 13px 4px 15px ${({ theme }) => theme.colors.border.color};
  z-index: 10;
  background: ${({ theme }) => theme.colors.background.primary};
`;

const DEFAULT_DESKTOP_WIDTH = '470px';
const MOBILE_WIDTH = '95vw';

export interface SideCartSlots {
  /** Slim banner above the header — funnel promotions, urgency messages */
  topBanner?: React.ReactNode;
  /** Custom header content (rendered below the default CartHeader) */
  header?: React.ReactNode;
  /** Content between the total and the checkout button — upsell offers, bundles */
  offerFooter?: React.ReactNode;
  /** Content below the checkout button — trust badges, links, policies */
  menuFooter?: React.ReactNode;
}

export interface SideCartProps {
  cartToken: string | null;
  isOpen: boolean;
  onClose: () => void;
  onCheckout?: (cartToken: string) => void;
  onCartUpdate?: (cartToken: string) => void;
  onCartCompleted?: () => void;
  isMobile?: boolean;
  /** Which CartSummaryV2 layout to use. Defaults to 'free-shipping-bar'. */
  summaryVariant?: CartSummaryVariant;
  /** Configurable content slots for funnel experiences and extensions */
  slots?: SideCartSlots;
}

export function SideCart({
  cartToken,
  isOpen,
  onClose,
  onCheckout,
  onCartUpdate,
  onCartCompleted,
  isMobile = false,
  summaryVariant = 'free-shipping-bar',
  slots,
}: SideCartProps) {
  const { t } = useTranslation('sidecart');
  const { countryConfig } = useIntl();
  const { data: cart, isLoading, error: fetchError } = useCart(cartToken, onCartCompleted);
  const updateCartItemMutation = useUpdateCartItem();
  const removeCartItemMutation = useRemoveCartItem();
  const updatePackQuantityMutation = useUpdatePackQuantity();
  const removePackMutation = useRemovePackFromCart();
  const { emitCartUpdated } = useFunnelStepEmitters();
  const cartMutationLoading =
    updateCartItemMutation.isPending ||
    removeCartItemMutation.isPending ||
    updatePackQuantityMutation.isPending ||
    removePackMutation.isPending;
  const priceOptions = {
    currency: countryConfig.currency.code,
    locale: countryConfig.locale,
    symbolOverride: countryConfig.currency.format,
  };

  const handleQuantityChange = async (
    productId: string,
    variantId: string,
    quantity: number,
    lineId?: string,
  ) => {
    if (!cartToken) {
      return;
    }

    try {
      const result = await updateCartItemMutation.mutateAsync({
        cartToken,
        productId,
        variantId,
        quantity,
        lineId,
      });

      const previousItem = cart?.items.find(
        (item) =>
          (lineId ? item.line_id === lineId : true) &&
          item.product_id === productId &&
          item.variant_id === variantId,
      );
      const delta = quantity - (previousItem?.quantity ?? 0);
      const updatedItem = result.cart.items.find(
        (item) =>
          (lineId ? item.line_id === lineId : true) &&
          item.product_id === productId &&
          item.variant_id === variantId,
      );
      const eventId = delta > 0 && updatedItem
        ? trackAddToCartFromLines({
            currency: result.cart.currency,
            cartId: result.cart.token,
            lines: [{
              product_id: updatedItem.product_id,
              variant_id: updatedItem.variant_id,
              quantity: delta,
              name: updatedItem.name,
              price: {
                display: updatedItem.price.display * delta,
              },
              image_url: updatedItem.image_url,
            }],
          })
        : null;
      if (delta !== 0) {
        void emitCartUpdated({
          pageContext: toFunnelCartPageContext(result.cart),
          behavioralEvent: delta > 0 ? 'add_to_cart' : 'remove_from_cart',
          params: { productId, variantId, quantity, delta, ...(lineId ? { lineId } : {}) },
          ...(eventId
            ? {
                businessEventId: buildBrowserBusinessEventId(
                  EVENT_TYPES.PRODUCT_ADDED_TO_CART,
                  eventId,
                ),
              }
            : {}),
        });
      }

      if (onCartUpdate) {
        onCartUpdate(result.cart.token);
      }
    } catch (error) {
      storefrontLayoutLogger.error('Failed to update cart item', error instanceof Error ? error : undefined, {
        productId,
        variantId,
        quantity,
      });
    }
  };

  const handlePackQuantityChange = async (packId: string, quantity: number) => {
    if (!cartToken) {
      return;
    }

    try {
      const result = await updatePackQuantityMutation.mutateAsync({
        cartToken,
        packId,
        quantity,
      });

      const previousPackItems = cart?.items.filter((item) => item.pack_id === packId) ?? [];
      const updatedPackItems = result.cart.items.filter((item) => item.pack_id === packId);
      const previousQuantities = new Map(
        previousPackItems.map((item) => [
          item.pack_item_id ?? `${item.product_id}:${item.variant_id}`,
          item.quantity,
        ]),
      );
      const addedLines = updatedPackItems.flatMap((item) => {
        const key = item.pack_item_id ?? `${item.product_id}:${item.variant_id}`;
        const lineDelta = item.quantity - (previousQuantities.get(key) ?? 0);
        if (lineDelta <= 0) return [];
        return [{
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: lineDelta,
          name: item.name,
          price: { display: item.price.display * lineDelta },
          image_url: item.image_url,
        }];
      });
      const previousPackItemCount = previousPackItems.reduce((sum, item) => sum + item.quantity, 0);
      const updatedPackItemCount = updatedPackItems.reduce((sum, item) => sum + item.quantity, 0);
      const itemDelta = updatedPackItemCount - previousPackItemCount;
      const eventId = addedLines.length > 0
        ? trackAddToCartFromLines({
            currency: result.cart.currency,
            cartId: result.cart.token,
            lines: addedLines,
          })
        : null;
      if (itemDelta !== 0) {
        void emitCartUpdated({
          pageContext: toFunnelCartPageContext(result.cart),
          behavioralEvent: itemDelta > 0 ? 'add_to_cart' : 'remove_from_cart',
          params: { packId, quantity, itemDelta },
          ...(eventId
            ? {
                businessEventId: buildBrowserBusinessEventId(
                  EVENT_TYPES.PRODUCT_ADDED_TO_CART,
                  eventId,
                ),
              }
            : {}),
        });
      }

      if (onCartUpdate) {
        onCartUpdate(result.cart.token);
      }
    } catch (error) {
      storefrontLayoutLogger.error(
        'Failed to update pack quantity',
        error instanceof Error ? error : undefined,
        { packId, quantity },
      );
    }
  };

  const handlePackRemove = async (packId: string) => {
    if (!cartToken) {
      return;
    }

    try {
      const result = await removePackMutation.mutateAsync({ cartToken, packId });

      void emitCartUpdated({
        pageContext: toFunnelCartPageContext(result.cart),
        behavioralEvent: 'remove_from_cart',
        params: { packId },
      });

      if (onCartUpdate) {
        onCartUpdate(result.cart.token);
      }
    } catch (error) {
      storefrontLayoutLogger.error(
        'Failed to remove pack from cart',
        error instanceof Error ? error : undefined,
        { packId },
      );
    }
  };

  const handleRemove = async (productId: string, variantId: string, lineId?: string) => {
    if (!cartToken) {
      return;
    }

    try {
      const result = await removeCartItemMutation.mutateAsync({
        cartToken,
        productId,
        variantId,
        lineId,
      });

      void emitCartUpdated({
        pageContext: toFunnelCartPageContext(result.cart),
        behavioralEvent: 'remove_from_cart',
        params: { productId, variantId, ...(lineId ? { lineId } : {}) },
      });

      if (onCartUpdate) {
        onCartUpdate(result.cart.token);
      }
    } catch (error) {
      storefrontLayoutLogger.error('Failed to remove cart item', error instanceof Error ? error : undefined, {
        productId,
        variantId,
      });
    }
  };

  const handleCheckout = () => {
    if (cartToken && onCheckout) {
      onCheckout(cartToken);
    }
  };

  const cartItemsQuantity = cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const pricing = cart?.pricing;
  const total = pricing?.subtotal.display ?? (cart ? cart.totals.total.display : 0);
  const compareAtTotal =
    pricing?.compareAtSubtotal?.display ??
    (cart
      ? cart.items.reduce((sum, item) => {
          const compareAt = item.compare_at_price?.display ?? item.price.display;
          return sum + compareAt * item.quantity;
        }, 0)
      : undefined);
  const savings = pricing
    ? pricingSavingsCents({
        lineDiscounts: pricing.lineDiscounts,
        orderDiscounts: pricing.orderDiscounts,
        compareAtSubtotal: pricing.compareAtSubtotal,
        subtotal: pricing.subtotal,
      })
    : cart
      ? cart.totals.discounts.display
      : 0;
  const appliedDiscountRows = pricing
    ? flattenCartDiscountRows({
        lineAppliedDiscounts: pricing.lines?.flatMap((line) => line.appliedDiscounts ?? []) ?? [],
        orderAppliedDiscounts: pricing.orderAppliedDiscounts ?? [],
      })
    : undefined;
  const isProceedDisabled = !cart || total <= 0 || isLoading;

  return (
    <DrawerWrapper>
      <Drawer
        isOpen={isOpen}
        onClose={isOpen ? onClose : undefined}
        width={isMobile ? MOBILE_WIDTH : DEFAULT_DESKTOP_WIDTH}
        role="dialog"
        aria-modal="true"
        aria-label={t(SIDE_CART_ACCESSIBILITY_LABEL)}
      >
        <Main>
            {isLoading ? (
              <>
                <CartHeader itemCount={0} onClose={onClose} />
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  {t(SIDE_CART_LOADING)}
                </div>
              </>
            ) : !cart || cart.items.length === 0 ? (
              <EmptyCart
                onClose={onClose}
                onContinueShopping={onClose}
              />
            ) : (
              <>
                {/* Slot: topBanner — slim promotional banner above everything */}
                {slots?.topBanner}

                <CartHeader
                  itemCount={cartItemsQuantity}
                  onClose={onClose}
                />

                {/* Slot: header — custom content below the cart header */}
                {slots?.header}

                <Lines>
                  <CartItems
                    items={cart.items}
                    isLoading={cartMutationLoading}
                    onQuantityChange={handleQuantityChange}
                    onRemove={handleRemove}
                    onPackQuantityChange={handlePackQuantityChange}
                    onPackRemove={handlePackRemove}
                  />
                </Lines>

                <Footer>
                  <CartSummaryV2
                    variant={summaryVariant}
                    total={total}
                    compareAtTotal={compareAtTotal}
                    savings={savings}
                    appliedDiscountRows={appliedDiscountRows}
                    onCheckout={handleCheckout}
                    isCheckoutDisabled={isProceedDisabled}
                    error={fetchError ? String(fetchError) : null}
                    priceOptions={priceOptions}
                    offerFooter={slots?.offerFooter}
                    menuFooter={slots?.menuFooter}
                  />
                </Footer>
              </>
            )}
        </Main>
      </Drawer>
    </DrawerWrapper>
  );
}
